import { useState } from "react";
import { FaCheckCircle, FaPaperPlane } from "react-icons/fa";
import { Input, Textarea } from "../ui/Input";
import Button from "../ui/Button";
import { useQuote } from "../../context/QuoteContext";
import { submitQuoteRequest } from "../../services/quoteService";
import { stripHtml } from "../../utils/richText";

const initialForm = {
  companyName: "",
  customerName: "",
  email: "",
  phone: "",
  gstNumber: "",
  address: "",
};

export default function QuoteForm() {
  const { items, clearQuote } = useQuote();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await submitQuoteRequest({ ...form, selectedProducts: items });
      if (response.ok) {
        setStatus(response.referenceId);
        setWhatsappUrl(response.whatsappUrl || "");
        if (response.whatsappUrl) {
          window.open(response.whatsappUrl, "_blank", "noopener,noreferrer");
        }
        setForm(initialForm);
        clearQuote();
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to submit enquiry right now.");
    } finally {
      setSubmitting(false);
    }
  };

  if (status) {
    return (
      <div className="rounded-xl border border-green-100 bg-green-50 p-8 text-center">
        <FaCheckCircle className="mx-auto text-5xl text-success" />
        <h2 className="mt-4 text-3xl font-bold text-dark">Thank you!</h2>
        <p className="mx-auto mt-3 max-w-2xl leading-7 text-body">Our sales team will contact you shortly with the best quotation.</p>
        <p className="mt-4 text-sm font-semibold text-success">Reference ID: {status}</p>
        {whatsappUrl && (
          <a href={whatsappUrl} target="_blank" rel="noreferrer" className="mt-6 inline-flex min-h-11 items-center justify-center rounded-xl bg-success px-5 py-3 text-sm font-bold text-white transition hover:bg-green-700">
            Send Quotation Details on WhatsApp
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-xl border border-line bg-white p-5 shadow-soft sm:p-8">
      <div className="grid gap-5 md:grid-cols-2">
        <Input id="companyName" label="Company Name" optional value={form.companyName} onChange={(event) => update("companyName", event.target.value)} />
        <Input id="customerName" label="Customer Name" value={form.customerName} onChange={(event) => update("customerName", event.target.value)} required />
        <Input id="email" label="Email" optional type="email" value={form.email} onChange={(event) => update("email", event.target.value)} />
        <Input id="phone" label="Phone" type="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} required />
        <Input id="gstNumber" label="GST Number" optional value={form.gstNumber} onChange={(event) => update("gstNumber", event.target.value)} />
      </div>
      <div className="mt-5 grid gap-5">
        <Textarea id="address" label="Address" optional value={form.address} onChange={(event) => update("address", event.target.value)} />
        <div className="rounded-xl bg-slate-50 p-4">
          <h3 className="mb-3 font-semibold text-dark">Selected Products</h3>
          {items.length ? (
            <div className="grid gap-2">
              {items.map((item) => (
                <div key={item.quoteKey || item.id} className="flex flex-wrap justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm">
                  <span className="font-bold text-dark">
                    {stripHtml(item.name)}
                    {item.selectedVariant?.label ? (
                      <small className="block font-semibold text-slate-500">
                        {item.optionGroupName || "Option"}: {item.selectedVariant.label}
                      </small>
                    ) : null}
                  </span>
                  <span className="font-semibold text-primary">Qty {item.quantity} {item.unit || "pcs"}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No products selected. You can still submit a general enquiry.</p>
          )}
        </div>
        {error && <p className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-danger">{error}</p>}
        <Button type="submit" className="w-full sm:w-auto" disabled={submitting}><FaPaperPlane /> {submitting ? "Submitting..." : "Submit Quote Request"}</Button>
      </div>
    </form>
  );
}
